import { bookingTrackingLinks } from "@/lib/carrierTracking";


// ─────────────────────────────────────────────────────────────────────
// Phase 2L-A144-fu-A — Admin bookings list shipping filter helpers.
//
// The `bookings.shipping_integration_status` field is written by TWO
// separate subsystems with slightly different vocabularies:
//
//   1. The posted-kit checkout / admin_create_booking path — writes
//      the A144 status set: awaiting_fulfilment, fulfilment_triggered,
//      labels_generated, labels_printed, kit_packed, kit_posted, error.
//
//   2. The Starshipit adapter (`backend/starshipit_service.py`) —
//      writes the AusPost-shaped set: order_created, print_requested,
//      printed, label_created, manifested, dispatched, in_transit,
//      out_for_delivery, delivered, exception, failed,
//      manual_action_required.
//
// Both vocabularies land on the same `shipping_integration_status`
// column, so the admin list has to normalise them into the 5 buckets
// the user actually cares about when they're triaging kits:
//
//   • Awaiting fulfilment  — paid, no label yet
//   • Label created        — label made / packed, not yet in the post
//   • Shipped              — in transit / handed to AusPost
//   • Delivered            — reached the customer (may or may not
//                            have the sample back at the lab yet)
//   • Failed / needs action — anything requiring manual intervention
//
// Any change to this mapping should ALSO be reflected in
// `backend/starshipit_service.py` STATUS_* constants and the
// `BookingDetail.jsx` INTEGRATION_STATUS_LABELS so the per-booking
// panel and the list view keep agreeing.
// ─────────────────────────────────────────────────────────────────────

export const SHIPPING_BUCKETS = [
  {
    key:       "awaiting_fulfilment",
    label:     "Awaiting fulfilment",
    short:     "Awaiting",
    chipColor: "bg-amber-100 text-amber-800",
    pillColor: "bg-amber-50 text-amber-700 border border-amber-200",
    tone:      "amber",
  },
  {
    key:       "label_created",
    label:     "Label created",
    short:     "Label",
    chipColor: "bg-cyan-100 text-cyan-800",
    pillColor: "bg-cyan-50 text-cyan-700 border border-cyan-200",
    tone:      "cyan",
  },
  {
    key:       "shipped",
    label:     "Shipped",
    short:     "Shipped",
    chipColor: "bg-blue-100 text-blue-800",
    pillColor: "bg-blue-50 text-blue-700 border border-blue-200",
    tone:      "blue",
  },
  {
    key:       "delivered",
    label:     "Delivered",
    short:     "Delivered",
    chipColor: "bg-emerald-100 text-emerald-800",
    pillColor: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    tone:      "emerald",
  },
  {
    key:       "failed",
    label:     "Failed · needs action",
    short:     "Needs action",
    chipColor: "bg-rose-100 text-rose-800",
    pillColor: "bg-rose-50 text-rose-700 border border-rose-200",
    tone:      "rose",
  },
];

export const SHIPPING_BUCKET_MAP = Object.fromEntries(
  SHIPPING_BUCKETS.map((b) => [b.key, b]),
);

const STATUS_TO_BUCKET = {
  // A144 posted-kit vocabulary
  awaiting_fulfilment:    "awaiting_fulfilment",
  fulfilment_triggered:   "awaiting_fulfilment",
  labels_generated:       "label_created",
  labels_printed:         "label_created",
  kit_packed:             "label_created",
  kit_posted:             "shipped",
  error:                  "failed",

  // Starshipit vocabulary (see /app/backend/starshipit_service.py)
  pending:                "awaiting_fulfilment",
  order_created:          "label_created",
  label_created:          "label_created",
  print_requested:        "label_created",
  printed:                "label_created",
  manifested:             "label_created",
  dispatched:             "shipped",
  in_transit:             "shipped",
  out_for_delivery:       "shipped",
  delivered:              "delivered",
  exception:              "failed",
  failed:                 "failed",
  manual_action_required: "failed",
  // Sep 2026 — the OVERALL two-label vocabulary was missing here, so a
  // half-failed booking hid inside "Awaiting fulfilment" and a finished
  // one never showed as done. See STATUS_* in starshipit_service.py.
  complete:               "label_created",
  partial_failed:         "failed",
  in_progress:            "awaiting_fulfilment",
};

// A booking is "shipping-relevant" if it has any posted-kit signal —
// non-posted-kit bookings (lab pickup / existing bottle / admin
// supplied) must never surface in shipping counts, filters, or the
// shipping column. This mirrors `PostedKitFulfilmentPanel.shouldShow`
// in `BookingDetail.jsx`.
export function isShippingRelevant(booking) {
  if (!booking) return false;
  const status = booking.shipping_integration_status;
  return !!(
    booking.posted_kit_paid_at_creation
    || (status && status !== "not_required")
    || booking.posted_kit_fee_cents
    || booking.starshipit_order_id
  );
}

// Normalise a booking's raw shipping status into one of the 5 buckets.
// Returns `null` when the booking is not shipping-relevant so the
// admin list can hide the shipping cell entirely for lab-pickup rows.
export function shippingBucket(booking) {
  if (!isShippingRelevant(booking)) return null;
  const raw = (booking.shipping_integration_status || "").toLowerCase();
  if (STATUS_TO_BUCKET[raw]) return STATUS_TO_BUCKET[raw];
  // Booking is shipping-relevant but the integration hasn't stamped a
  // status yet (e.g. paid at creation but the fulfilment event hasn't
  // fired, or a raw admin trigger before Starshipit responded).
  // Fall back to "awaiting fulfilment" so the row is visible in the
  // most useful bucket rather than falling into an "unknown" hole.
  return "awaiting_fulfilment";
}

// True when the kit reached the customer but no sample has been
// received back at the lab yet. Used by the "Delivered" pill in the
// bookings list to add a subtle "sample not returned yet" hint —
// answers the admin question "which kits are stuck at the customer?".
export function isAwaitingSampleReturn(booking) {
  if (shippingBucket(booking) !== "delivered") return false;
  return !booking.sample_received_date && !booking.date_sample_received;
}

// Sep 2026 — tracking links are CARRIER-AWARE and built in one place
// (`lib/carrierTracking.js`): the kit ships Aramex, the sample returns on
// AusPost Express, so a single hard-coded AusPost URL was wrong half the
// time. Kept as a thin wrapper for callers that only want the outbound href.
export function shippingTrackingHref(booking) {
  const [outbound] = bookingTrackingLinks(booking);
  return outbound?.href || null;
}

// Preferred short tracking number to show in the shipping column.
export function shippingTrackingNumber(booking) {
  return booking.starshipit_tracking_number
    || booking.outbound_tracking_number
    || booking.kit_tracking_number
    || null;
}
